import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  clearHierarchyLevel,
  downloadShapefile,
  getHierarchyCounts,
  importHierarchy,
  listHierarchyImports,
  undoHierarchyImport,
} from "@/lib/api";
import type { HierarchyDeleteSummary } from "@/lib/api";
import type {
  HierarchyImportRecord,
  HierarchyImportResult,
  HierarchyLevel,
} from "@/lib/types";
import { Banner, Button, ConfirmDialog, Panel, Spinner } from "./ui";
import HierarchyShapeUpdateSection from "./HierarchyShapeUpdateSection";
import { useLanguage } from "@/i18n";
import type { Translations } from "@/i18n/en";
import { formatDate } from "@/lib/format";

const LEVELS: {
  level: HierarchyLevel;
  label: string;
  hint: string;
}[] = [
  { level: "zone", label: "Zones", hint: "Import first" },
  { level: "ward", label: "Wards", hint: "Placed inside zones" },
  { level: "block", label: "Blocks", hint: "Placed inside wards" },
];

/** A hierarchy upload: everything this panel deals with. Property-outline
 *  imports carry level "feature" and are handled by PropertyImportPanel. */
type HierarchyUpload = HierarchyImportRecord & { level: HierarchyLevel };

export default function HierarchyImportPanel({
  projectId,
  refreshKey,
  onImported,
}: {
  projectId: number;
  /** Bump to re-read the counts after the parent changes the hierarchy by some
   *  other route (adding a zone by hand, for instance). */
  refreshKey?: unknown;
  /** Fired after a successful import OR clear so the parent refetches. */
  onImported: () => void;
}) {
  const { t, lang } = useLanguage();
  const [busyLevel, setBusyLevel] = useState<HierarchyLevel | null>(null);
  const [clearingLevel, setClearingLevel] = useState<HierarchyLevel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<HierarchyImportResult[]>([]);
  // Project-wide totals, fetched here rather than passed in: the parent only has
  // the SCOPED lists (wards of the selected zone, blocks of the selected ward),
  // so using those made this control read "0 wards" and hide itself on a project
  // that plainly had wards, whenever no zone was selected.
  const [counts, setCounts] = useState<{ zone: number; ward: number; block: number }>({
    zone: 0,
    ward: 0,
    block: 0,
  });

  const reloadCounts = useCallback(() => {
    getHierarchyCounts(projectId)
      .then(setCounts)
      .catch(() => {
        /* Non-fatal: importing still works, the remove links just stay hidden. */
      });
  }, [projectId]);

  const reloadImports = useCallback(async () => {
    try {
      const all = await listHierarchyImports(projectId);
      // Property-outline uploads share this table but belong to their own
      // panel: they create field features rather than hierarchy rows, and
      // undoing one destroys survey data instead of imported geometry.
      //
      // remaining === 0 is dropped as well. The backend prunes spent records on
      // every delete, but a row can also reach zero by a path that does not --
      // deleting shapes one at a time, say -- and "0 of 3104 still present"
      // reads as "this file is still imported" when nothing of it remains.
      const mine = all.filter(
        (i): i is HierarchyUpload => i.level !== "feature" && i.remaining > 0
      );
      setImports(mine);
      return mine;
    } catch {
      // Empty rather than null on failure, so the list renders its "nothing
      // recorded" state instead of a spinner that never resolves.
      setImports([]);
      return [];
    }
  }, [projectId]);

  /**
   * Uploads made since this page loaded -- the only ones that offer an undo.
   *
   * Held in component state rather than read back from the server, which is
   * what makes it vanish on refresh. Undo is for the file you just picked by
   * mistake; on every historical upload it becomes a one-click way to delete a
   * hierarchy that has since been surveyed, which is what "Remove all" is for
   * -- and that states the cascade and demands an export first.
   */
  const [sessionImportIds, setSessionImportIds] = useState<number[]>([]);

  useEffect(reloadCounts, [reloadCounts, refreshKey]);
  useEffect(() => {
    void reloadImports();
  }, [reloadImports, refreshKey]);
  // One input per level, so picking a Wards file can't be mistaken for a
  // Zones file -- the level is decided by which control you used, not by a
  // separate dropdown that can drift out of sync with the file you chose.
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  /**
   * The clear-level flow, as a small state machine.
   *
   * Two stages because the backend answers the question we cannot: whether
   * survey data is in the way. Stage "confirm" is the plain "are you sure";
   * stage "blocked" only appears once the server has refused and told us
   * exactly what would be lost, and it gates Delete behind an export.
   */
  const [clearFlow, setClearFlow] = useState<{
    level: HierarchyLevel;
    label: string;
    stage: "confirm" | "blocked";
    summary?: HierarchyDeleteSummary;
    /** Set when undoing ONE upload rather than clearing a whole level. Both go
     *  through the same two stages and the same export gate, because both can
     *  destroy field work and the backend refuses them identically. */
    importRecord?: HierarchyUpload;
  } | null>(null);
  /** Set once an export of this project has actually downloaded, which is what
   *  unlocks the destructive button. Deliberately not persisted: reopening the
   *  dialog should export again, since the data may have changed since. */
  const [exportedForClear, setExportedForClear] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** A file chosen for a level that already holds data, awaiting confirmation. */
  /** Uploads recorded for this project. Null while loading.
   *
   *  Narrowed to exclude "feature": property-outline uploads share the same
   *  table but belong to PropertyImportPanel, and clearFlow's level is a
   *  HierarchyLevel -- so filtering them out is a type guarantee here, not just
   *  a runtime filter the compiler has to be told about at every use. */
  const [imports, setImports] = useState<HierarchyUpload[] | null>(null);

  const [importWarning, setImportWarning] = useState<{
    level: HierarchyLevel;
    label: string;
    file: File;
    existing: number;
  } | null>(null);

  function handleFile(level: HierarchyLevel, files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!/\.(geo)?json$/i.test(file.name)) {
      setError(t.hierarchyImportPanel.onlyGeojsonAccepted);
      resetInput(level);
      return;
    }
    // Importing into a level that already holds rows ADDS to it. Shapes
    // already present are skipped rather than duplicated, so re-uploading a
    // corrected file is safe -- but a file covering a different area appends
    // to what is there rather than replacing it, which is almost never what
    // someone re-picking a file intends. Confirmed rather than discovered
    // afterwards in the counts.
    const existing = counts[level] ?? 0;
    if (existing > 0) {
      const label = LEVELS.find((l) => l.level === level)?.label ?? level;
      setImportWarning({ level, label, file, existing });
      return;
    }
    void runImport(level, file);
  }

  function resetInput(level: HierarchyLevel) {
    const input = inputRefs.current[level];
    if (input) input.value = "";
  }

  async function runImport(level: HierarchyLevel, file: File) {
    setImportWarning(null);
    setBusyLevel(level);
    setError(null);
    // Ids present before the upload, so the new record can be told apart from
    // them -- the import response carries counts but not its own id.
    const before = new Set((imports ?? []).map((i) => i.id));
    try {
      const result = await importHierarchy(projectId, level, file);
      setResults((prev) => [result, ...prev].slice(0, 4));
      reloadCounts();
      const after = await reloadImports();
      setSessionImportIds((prev) => [
        ...prev,
        ...after.filter((i) => !before.has(i.id)).map((i) => i.id),
      ]);
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Couldn't import ${level}s.`);
    } finally {
      setBusyLevel(null);
      // Clearing the input matters: re-picking the same file after a failure
      // fires no change event otherwise, so a retry looks like a dead button.
      resetInput(level);
    }
  }

  /**
   * Clears everything one import created.
   *
   * Split into stages rather than done in one call, because whether survey data
   * is in the way is a question only the backend can answer: it is asked
   * without force first, and the refusal carries the exact counts. Those counts
   * are what the second stage shows, rather than anything guessed here.
   */
  /** Undo one upload. Shares the whole two-stage + export-gate flow with the
   *  level clear, since both destroy rows and can cost field work. */
  function startUndo(rec: HierarchyUpload) {
    setError(null);
    setExportedForClear(false);
    setClearFlow({
      level: rec.level,
      label: rec.filename || t.hierarchyImportPanel.thisUpload,
      stage: "confirm",
      importRecord: rec,
    });
  }

  function startClear(level: HierarchyLevel, label: string) {
    setError(null);
    setExportedForClear(false);
    setClearFlow({ level, label, stage: "confirm" });
  }

  /** First press: try without force. A refusal moves the dialog to stage two. */
  async function attemptClear() {
    if (!clearFlow) return;
    const { level, label } = clearFlow;
    setClearingLevel(level);
    setError(null);
    try {
      const rec = clearFlow.importRecord;
      const result = rec
        ? await undoHierarchyImport(projectId, rec.id, false)
        : await clearHierarchyLevel(projectId, level, false);
      if (result.blocked) {
        setClearFlow({
          level,
          label,
          stage: "blocked",
          summary: result.summary,
          importRecord: rec,
        });
        return;
      }
      finishClear(level, result.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.hierarchyImportPanel.couldntRemove(label));
      setClearFlow(null);
    } finally {
      setClearingLevel(null);
    }
  }

  /** Second press, only reachable after an export has succeeded. */
  async function forceClear() {
    if (!clearFlow) return;
    const { level, label } = clearFlow;
    setClearingLevel(level);
    setError(null);
    try {
      const rec = clearFlow.importRecord;
      const result = rec
        ? await undoHierarchyImport(projectId, rec.id, true)
        : await clearHierarchyLevel(projectId, level, true);
      if (result.blocked) throw new Error(t.hierarchyImportPanel.serverStillRefused);
      finishClear(level, result.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.hierarchyImportPanel.couldntRemove(label));
      setClearFlow(null);
    } finally {
      setClearingLevel(null);
    }
  }

  function finishClear(level: HierarchyLevel, s: HierarchyDeleteSummary) {
    const parts = [
      s.zones ? t.hierarchyImportPanel.zonesCount(s.zones) : null,
      s.wards ? t.hierarchyImportPanel.wardsCount(s.wards) : null,
      s.blocks ? t.hierarchyImportPanel.blocksCount(s.blocks) : null,
    ].filter(Boolean);
    setResults((prev) =>
      [
        {
          level,
          created: 0,
          skipped: 0,
          assigned_by_fallback: 0,
          source_crs: null,
          reprojected: false,
          messages: [
            parts.length
              ? t.hierarchyImportPanel.removedSummary(parts.join(", "))
              : t.hierarchyImportPanel.removedSummary(t.hierarchyImportPanel.removedNothingAlreadyEmpty),
          ],
        } as HierarchyImportResult,
        ...prev,
      ].slice(0, 4)
    );
    setClearFlow(null);
    reloadCounts();
    void reloadImports();
    onImported();
  }

  /**
   * Downloads the project's shapes, and only then unlocks the delete.
   *
   * Export is required rather than merely offered: this is the one action in
   * the portal that destroys field work in bulk, and a shape a surveyor walked
   * to cannot be recreated from anything the office holds. The flag resets
   * whenever the dialog reopens, so the file always matches what is about to
   * be deleted.
   *
   * Whole-project scope because that is what the API supports and what the
   * level-clear actually removes -- delete_level guards on every block in the
   * project regardless of which level was named.
   */
  async function exportBeforeClear() {
    setExporting(true);
    setError(null);
    try {
      await downloadShapefile({ projectId });
      setExportedForClear(true);
    } catch (err) {
      // Nothing collected yet 404s here. That is not a failure to guard
      // against -- there is no field work to lose -- so the gate opens rather
      // than trapping the operator behind an export that cannot succeed.
      const text = err instanceof Error ? err.message : t.hierarchyImportPanel.exportFailedGeneric;
      if (/nothing to export/i.test(text)) {
        setExportedForClear(true);
      } else {
        setError(text);
      }
    } finally {
      setExporting(false);
    }
  }

  return (
    <Panel title={t.hierarchyImportPanel.panelTitle}>
      <div className="p-3">
        <p className="mb-3 text-2xs text-ink-muted">
          {t.hierarchyImportPanel.intro}
        </p>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {LEVELS.map(({ level, label, hint }) => {
            const busy = busyLevel === level;
            const clearing = clearingLevel === level;
            const count = counts[level];
            return (
              <div
                key={level}
                className={clsx(
                  "rounded border border-dashed px-3 py-3 text-center transition",
                  busy
                    ? "border-accent bg-accent-subtle"
                    : "border-line-strong hover:border-ink-muted hover:bg-surface-sunken"
                )}
              >
                <input
                  ref={(el) => {
                    inputRefs.current[level] = el;
                  }}
                  type="file"
                  accept=".geojson,.json"
                  className="hidden"
                  onChange={(e) => void handleFile(level, e.target.files)}
                />
                <p className="text-xs font-medium text-ink">{label}</p>
                <p className="mt-0.5 text-2xs text-ink-muted">{hint}</p>
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busyLevel !== null || clearingLevel !== null}
                    onClick={() => inputRefs.current[level]?.click()}
                  >
                    {busy ? <Spinner className="h-3 w-3" /> : null}
                    {busy ? t.hierarchyImportPanel.importing : t.hierarchyImportPanel.chooseFile}
                  </Button>
                </div>
                {/* Removing the whole import lives with the control that created
                    it, rather than as a per-row action in the lists below: an
                    import is one file producing a whole level, so the undo has
                    to match that unit. Only offered when there is something to
                    remove, so a fresh project shows nothing extra. */}
                {count > 0 && (
                  <button
                    onClick={() => startClear(level, label)}
                    disabled={busyLevel !== null || clearingLevel !== null}
                    className="mt-1.5 text-2xs text-ink-muted underline-offset-2 transition hover:text-status-text-critical hover:underline disabled:opacity-40"
                  >
                    {clearing
                      ? t.hierarchyImportPanel.removing
                      : t.hierarchyImportPanel.removeAll(count, label.toLowerCase())}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        {results.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {results.map((r, i) => (
              <li
                key={`${r.level}-${i}`}
                className="rounded bg-surface-raised px-3 py-2 text-2xs shadow-ring"
              >
                <span className="font-medium text-ink">
                  {r.created} {r.level}
                  {r.created === 1 ? "" : "s"} created
                </span>
                {r.skipped > 0 && (
                  <span className="ml-2 text-status-text-critical">
                    {t.hierarchyImportPanel.skippedCount(r.skipped)}
                  </span>
                )}
                {r.assigned_by_fallback > 0 && (
                  <span className="ml-2 text-ink-muted">
                    {t.hierarchyImportPanel.placedByFallback(r.assigned_by_fallback)}
                  </span>
                )}
                {r.reprojected && r.source_crs && (
                  <span className="ml-2 text-ink-muted">
                    {t.hierarchyImportPanel.reprojectedFromCrs(r.source_crs)}
                  </span>
                )}
                {r.messages.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-ink-muted">
                    {r.messages.map((m, mi) => (
                      <li key={mi}>· {m}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Recorded uploads, each undoable on its own.
            Before this existed the only undo was "remove every zone/ward/block
            in the project", so correcting one bad file meant re-importing all
            of them. Imports made before tracking shipped have no record, which
            the note below states rather than leaving the list looking
            incomplete for no reason. */}
        <div className="mt-4 border-t border-line pt-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">
            {t.hierarchyImportPanel.uploadsTitle}
          </p>

          {imports === null ? (
            <p className="mt-2 flex items-center gap-2 text-2xs text-ink-muted">
              <Spinner className="h-3 w-3" /> {t.hierarchyImportPanel.loadingUploads}
            </p>
          ) : imports.length === 0 ? (
            <p className="mt-1.5 text-2xs text-ink-muted">
              {t.hierarchyImportPanel.noUploadsRecorded}
            </p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {imports.map((imp) => (
                <li
                  key={imp.id}
                  className="flex items-center justify-between gap-2 rounded bg-surface-raised px-3 py-1.5 text-2xs shadow-ring"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">
                      {imp.filename ?? t.hierarchyImportPanel.thisUpload}
                    </p>
                    <p className="text-ink-muted">
                      {imp.level} ·{" "}
                      {/* What an undo would remove NOW, not what the upload
                          originally made -- rows can have gone by other paths. */}
                      {imp.remaining} of {imp.created_count}
                      {imp.created_by ? ` · ${imp.created_by}` : ""} ·{" "}
                      {formatDate(imp.created_at, lang, {
                        day: "2-digit",
                        month: "short",
                        year: "2-digit",
                      })}
                    </p>
                  </div>
                  {sessionImportIds.includes(imp.id) && (
                    <Button
                      size="sm"
                      variant="danger"
                      className="h-7 shrink-0 px-2 text-2xs"
                      disabled={busyLevel !== null || clearingLevel !== null}
                      onClick={() => startUndo(imp)}
                    >
                      {t.common.undo}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {sessionImportIds.length > 0 && (
            <p className="mt-1.5 text-2xs text-ink-muted">
              {t.hierarchyImportPanel.undoOfferNote}
            </p>
          )}
        </div>

        {/* Reshape / rename / re-parent existing rows from an edited export,
            without the delete-and-reimport that would cost the field data. */}
        {counts.zone + counts.ward + counts.block > 0 && (
          <HierarchyShapeUpdateSection
            projectId={projectId}
            refreshKey={refreshKey}
            onUpdated={onImported}
          />
        )}
      </div>

      {/* Stage one: the plain "are you sure", stating the cascade. That lower
          levels go too is not optional and not obvious -- blocks reference
          wards -- so it is said here rather than discovered afterwards. */}
      <ConfirmDialog
        open={clearFlow?.stage === "confirm"}
        title={
          clearFlow?.importRecord
            ? t.hierarchyImportPanel.undoThisUpload(
                clearFlow.importRecord.filename ?? t.hierarchyImportPanel.thisUpload
              )
            : t.hierarchyImportPanel.removeAllQuestion(clearFlow?.label.toLowerCase() ?? "")
        }
        body={
          <>
            {clearFlow?.importRecord
              ? t.hierarchyImportPanel.undoBodyTemplate(
                  clearFlow.importRecord.remaining,
                  clearFlow.importRecord.level
                )
              : t.hierarchyImportPanel.removeBodyTemplate(clearFlow?.label.toLowerCase() ?? "")}
            {clearFlow?.level === "zone" && t.hierarchyImportPanel.cascadeZone}
            {clearFlow?.level === "ward" && t.hierarchyImportPanel.cascadeWard}{" "}
            {t.hierarchyImportPanel.reimportToRestore}
          </>
        }
        confirmLabel={
          clearFlow?.importRecord
            ? t.hierarchyImportPanel.undoImportLabel
            : t.hierarchyImportPanel.removeLabel
        }
        // Gated even here, before the backend has said whether survey data is
        // in the way. Putting the gate only on the refusal (below) meant a
        // level whose blocks were all still "pending" deleted in one click with
        // no export offered at all -- and "no shapes collected YET" is not the
        // same as "nothing worth keeping", since the hierarchy itself took an
        // import to build and the operator may want the file either way.
        confirmDisabled={!exportedForClear}
        busy={clearingLevel !== null}
        onCancel={() => setClearFlow(null)}
        onConfirm={() => void attemptClear()}
      >
        <div className="rounded bg-surface-sunken p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-2xs text-ink-secondary">
              {exportedForClear
                ? t.hierarchyImportPanel.exportedCanRemove
                : t.hierarchyImportPanel.exportFieldDataFirst}
            </span>
            <Button
              size="sm"
              variant={exportedForClear ? "secondary" : "primary"}
              onClick={() => void exportBeforeClear()}
              disabled={exporting}
            >
              {exporting ? <Spinner className="h-3 w-3" /> : null}
              {exporting
                ? t.common.preparing
                : exportedForClear
                  ? t.common.exportAgain
                  : t.common.exportShapefile}
            </Button>
          </div>
        </div>
      </ConfirmDialog>

      {/* Stage two: the backend refused because field work would be lost. The
          counts are its own, and Delete stays locked until an export has
          actually downloaded -- see exportBeforeClear for why it is required
          rather than merely offered. */}
      <ConfirmDialog
        open={clearFlow?.stage === "blocked"}
        title={t.hierarchyImportPanel.destroysFieldWorkTitle}
        body={
          <>
            {t.hierarchyImportPanel.destroysFieldWorkBody1}{" "}
            <span className="font-medium text-ink">
              {clearFlow?.summary?.features ?? 0} {t.hierarchyImportPanel.destroysFieldWorkBody2}
            </span>{" "}
            {t.hierarchyImportPanel.destroysFieldWorkBody3}{" "}
            <span className="font-medium text-ink">
              {clearFlow?.summary?.worked_blocks ?? 0} {t.hierarchyImportPanel.destroysFieldWorkBody4}
            </span>{" "}
            {t.hierarchyImportPanel.destroysFieldWorkBody5}
            {clearFlow?.summary?.rasters
              ? t.hierarchyImportPanel.destroysFieldWorkImagery(clearFlow.summary.rasters)
              : ""}
            {t.hierarchyImportPanel.destroysFieldWorkBody6}
          </>
        }
        confirmLabel={t.hierarchyImportPanel.deleteAnyway}
        confirmDisabled={!exportedForClear}
        busy={clearingLevel !== null}
        onCancel={() => setClearFlow(null)}
        onConfirm={() => void forceClear()}
      >
        <div className="rounded bg-surface-sunken p-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-2xs text-ink-secondary">
              {exportedForClear
                ? t.hierarchyImportPanel.exportedCanDelete
                : t.hierarchyImportPanel.exportRequiredDelete}
            </span>
            <Button
              size="sm"
              variant={exportedForClear ? "secondary" : "primary"}
              onClick={() => void exportBeforeClear()}
              disabled={exporting}
            >
              {exporting ? <Spinner className="h-3 w-3" /> : null}
              {exporting
                ? t.common.preparing
                : exportedForClear
                  ? t.common.exportAgain
                  : t.common.exportShapefile}
            </Button>
          </div>
          <p className="mt-1.5 text-2xs text-ink-muted">
            {t.hierarchyImportPanel.exportNoteWholeProject}
          </p>
        </div>
      </ConfirmDialog>

      {/* Re-importing adds to what is there. Shapes already present are
          skipped by geometry, so re-uploading a corrected file is safe. */}
      <ConfirmDialog
        open={importWarning !== null}
        danger={false}
        title={t.hierarchyImportPanel.alreadyHasTitle(
          importWarning?.existing ?? 0,
          importWarning?.label.toLowerCase() ?? ""
        )}
        body={
          <>
            {t.hierarchyImportPanel.importAddsPrefix}{" "}
            <span className="font-medium text-ink">{importWarning?.file.name}</span>{" "}
            <span className="font-medium text-ink">{t.hierarchyImportPanel.importAddsAddsTo}</span>{" "}
            {t.hierarchyImportPanel.importAddsSuffix}{" "}
            {importWarning?.label.toLowerCase()} {t.hierarchyImportPanel.importAddsFirst}
          </>
        }
        confirmLabel={t.hierarchyImportPanel.importAnyway}
        busy={busyLevel !== null}
        onCancel={() => {
          if (importWarning) resetInput(importWarning.level);
          setImportWarning(null);
        }}
        onConfirm={() =>
          importWarning && void runImport(importWarning.level, importWarning.file)
        }
      />
    </Panel>
  );
}
